import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  BaseInsightPayload,
  flattenAggregateData,
  getInsightAggregate,
  getInsightData,
  InsightDataItem,
  isAggregateOnlyInsightPayload,
} from 'types/store/normalized/insights/InsightDataPayload';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';

export class ExportEmptyChartError extends Error {}

export const useExport = () => {
  const dispatch = useDispatch();

  const exportInsightsToCSV = useCallback(
    async (name: string, data: InsightDataItem[]) => {
      try {
        if (!data || data.length === 0) {
          throw new ExportEmptyChartError();
        }

        const headers = Object.keys(data[0]);
        const rows = data.map((chart) =>
          headers.map(
            (header) => `"${String(chart[header]).replace(/"/g, '""')}"`
          )
        );

        const csvContent = [headers, ...rows]
          .map((row) => row.join(','))
          .join('\n');
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const date = new Date();
        const dayOfWeek = date.toLocaleDateString('en-US', {
          weekday: 'short',
        });
        const formattedDate = date
          .toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          })
          .replace(',', '');
        link.download = `insight_export_${name}_${dayOfWeek}_${formattedDate}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        let notificationMessageKey =
          'DASHBOARD_DETAIL_PAGE.FAILED_TO_EXPORT_DATA';
        if (error instanceof ExportEmptyChartError) {
          notificationMessageKey =
            'DASHBOARD_DETAIL_PAGE.NO_CHART_DATA_AVAILABLE_TO_EXPORT';
        }
        dispatch(
          showSnackbarNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get('ERROR_SNACKBAR_TITLE'),
            notificationMessage: intl.get(notificationMessageKey),
          })
        );
      }
    },
    [dispatch]
  );

  const exportChartData = useCallback(
    async (name: string, chartData: BaseInsightPayload) => {
      if (isAggregateOnlyInsightPayload(chartData)) {
        const aggregate = getInsightAggregate(chartData);
        const flattenedData = flattenAggregateData(aggregate || {});

        exportInsightsToCSV(name, flattenedData || []);
      } else {
        exportInsightsToCSV(name, getInsightData(chartData) || []);
      }
    },
    [exportInsightsToCSV]
  );

  const exportInsightsToPDF = useCallback(
    async (chartElement: HTMLElement, insightName: string) => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        const canvas = await html2canvas(chartElement, {
          scale: 2,
          useCORS: true,
          logging: false,
        });
        const imageData = canvas.toDataURL('image/png');
        const pdfWidth = canvas.width;
        const pdfHeight = canvas.height;
        const padding = 20;
        const doc = new jsPDF({
          orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
          unit: 'px',
          format: [pdfWidth + padding * 2, pdfHeight + padding * 2],
        });
        doc.addImage(
          imageData,
          'PNG',
          padding,
          padding,
          pdfWidth - padding * 2,
          pdfHeight - padding * 2
        );
        doc.save(`${insightName}.pdf`);
      } catch (error) {
        dispatch(
          showSnackbarNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get('ERROR_SNACKBAR_TITLE'),
            notificationMessage: intl.get(
              'DASHBOARD_DETAIL_PAGE.FAILED_TO_EXPORT_CHART_TO_PDF'
            ),
          })
        );
      }
    },
    [dispatch]
  );

  return {
    exportChartData,
    exportInsightsToPDF,
  };
};
